import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Checkbox,
  CodeChip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Skeleton,
  Switch,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import { UserRole } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Check,
  Plus,
  Power,
  ShieldCheck,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toBase64 } from '../features/configuration/upload';
import { ReadinessChecklist, scrollToSection, type Readiness } from '../features/chambers/ReadinessChecklist';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';

interface ChamberRow {
  id: string;
  slug: string;
  legalName: string;
  shortName: string;
  region: string | null;
  active: boolean;
  sandbox: boolean;
  wentLiveAt: string | null;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
  userCount: number;
  organisationCount: number;
  documentCount: number;
  documentTypeCount: number;
  createdAt: string;
}

interface DocumentTypeOption {
  id: string;
  code: string;
  name: string;
  agreement: string | null;
  preferential: boolean;
}

/**
 * Chambers (T-054).
 *
 * Onboarding is where this product either scales or turns into a consulting engagement.
 * Creating a chamber is a console operation, and so is taking it live — but only once
 * the server's own readiness checklist passes, which is why the go-live control lives
 * next to the checklist rather than on the list.
 */
export function Chambers() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: ['chambers'],
    queryFn: () => api.get<ChamberRow[]>('/api/console/platform/chambers'),
  });

  const rows = query.data ?? [];
  const pending = rows.filter((c) => !c.active && !c.sandbox);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Chambers"
        lede="Every chamber on the platform, its branding, the document types it is accredited to issue, and whether it is ready to go live."
        actions={
          <Button variant="primary" leadingIcon={<Plus className="size-3.5" />} onClick={() => setCreateOpen(true)}>
            Add a chamber
          </Button>
        }
      />

      {pending.length > 0 && (
        <Callout
          tone="info"
          icon={<Building2 className="size-4" />}
          title={`${pending.length} chamber${pending.length === 1 ? ' is' : 's are'} still being set up`}
        >
          {pending.map((c) => c.shortName).join(', ')} — created but not live. They cannot receive
          requests until their checklist passes and you activate them.
        </Callout>
      )}

      {query.isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-5" />}
          title="No chambers yet"
          description="A chamber is a tenant: its own operators, seals, numbering and branding, isolated from every other."
          action={
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              Add the first chamber
            </Button>
          }
        />
      ) : (
        <TableShell>
          <Table className="min-w-[860px]">
            <Thead>
              <Tr>
                <Th>Chamber</Th>
                <Th>Status</Th>
                <Th numeric>Doc types</Th>
                <Th numeric>Operators</Th>
                <Th numeric>Exporters</Th>
                <Th numeric>Certificates</Th>
                <Th>Live since</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id} onClick={() => navigate(`/platform/chambers/${row.id}`)}>
                  <Td>
                    <span className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="size-6 shrink-0 rounded-[var(--pv-radius-xs)]"
                        style={{
                          background: row.primaryColor,
                          boxShadow: `inset 0 -3px 0 ${row.secondaryColor}`,
                        }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{row.shortName}</span>
                        <span className="block truncate [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                          {row.region ?? row.legalName}
                        </span>
                      </span>
                    </span>
                  </Td>
                  <Td>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {row.active ? (
                        <Badge tone="ok" size="sm" dot>Live</Badge>
                      ) : (
                        <Badge tone="warn" size="sm" dot>Setting up</Badge>
                      )}
                      {row.sandbox && (
                        <Tooltip content="Certificates issued here are watermarked and never enter a real sequence">
                          <span>
                            <Badge tone="info" size="sm">Sandbox</Badge>
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  </Td>
                  <Td numeric>{row.documentTypeCount}</Td>
                  <Td numeric>{row.userCount}</Td>
                  <Td numeric>{row.organisationCount}</Td>
                  <Td numeric>{row.documentCount}</Td>
                  <Td>
                    <span className="pv-num [font-size:12.5px] text-[var(--pv-muted)]">
                      {row.wentLiveAt
                        ? new Date(row.wentLiveAt).toLocaleDateString('en-AU', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '—'}
                    </span>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}

      <CreateChamberDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

export function ChamberDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();

  const chambers = useQuery({
    queryKey: ['chambers'],
    queryFn: () => api.get<ChamberRow[]>('/api/console/platform/chambers'),
  });
  const readiness = useQuery({
    queryKey: ['chamber-readiness', id],
    queryFn: () => api.get<Readiness>(`/api/console/platform/chambers/${id}/readiness`),
    enabled: Boolean(id),
  });

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      api.post(`/api/console/platform/chambers/${id}/active`, { active }),
    onSuccess: (_result, active) => {
      toast.success(
        active ? 'Chamber is live' : 'Chamber taken offline',
        active
          ? 'It can now receive requests and issue certificates.'
          : 'Existing certificates are unaffected; no new requests will be accepted.',
      );
      void queryClient.invalidateQueries({ queryKey: ['chambers'] });
      void queryClient.invalidateQueries({ queryKey: ['chamber-readiness', id] });
    },
    onError: (error) =>
      // The server re-checks readiness at activation, so this is where a stale checklist
      // surfaces — worth showing verbatim rather than as "something went wrong".
      toast.error(
        'Could not change the chamber',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const chamber = chambers.data?.find((c) => c.id === id);

  // Arriving with #branding or #accreditation (from a checklist "Fix this" link) lands on
  // that section, once the page has rendered it.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash && chamber) scrollToSection(hash.slice(1));
  }, [hash, chamber]);

  if (chambers.isLoading) return <Skeleton className="h-96" />;
  if (!chamber) {
    return (
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title="Chamber not found">
        <Link to="/platform/chambers" className="underline">Back to chambers</Link>
      </Callout>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to="/platform/chambers"
            className="inline-flex items-center gap-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)] hover:text-[var(--pv-ink)]"
          >
            <ArrowLeft aria-hidden className="size-3.5" /> All chambers
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <h1 className="[font-size:var(--pv-text-h1)] font-bold tracking-[-0.02em]">
              {chamber.shortName}
            </h1>
            <CodeChip tone="leaf">{chamber.slug}</CodeChip>
            {chamber.active ? (
              <Badge tone="ok" size="sm" dot>Live</Badge>
            ) : (
              <Badge tone="warn" size="sm" dot>Setting up</Badge>
            )}
            {chamber.sandbox && <Badge tone="info" size="sm">Sandbox</Badge>}
          </div>
          <p className="mt-1 [font-size:13.5px] text-[var(--pv-muted)]">{chamber.legalName}</p>
        </div>

        {chamber.active ? (
          <Button
            variant="secondary"
            leadingIcon={<Power className="size-3.5" />}
            loading={setActive.isPending}
            onClick={() => setActive.mutate(false)}
          >
            Take offline
          </Button>
        ) : (
          <Tooltip
            content={
              readiness.data?.ready
                ? 'Every check passes — this chamber can issue real certificates'
                : 'The checklist below must pass first'
            }
          >
            <span>
              <Button
                variant="primary"
                leadingIcon={<ShieldCheck className="size-3.5" />}
                disabled={!readiness.data?.ready}
                loading={setActive.isPending}
                onClick={() => setActive.mutate(true)}
              >
                Take live
              </Button>
            </span>
          </Tooltip>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-4">
        <Card>
          <CardHeader
            title="Go-live checklist"
            description="Evaluated by the server, which re-checks it at activation. Nothing here can be waived from the console."
          />
          <ReadinessChecklist readiness={readiness.data} loading={readiness.isLoading} audience="platform" />
        </Card>

          <ChamberAdminsCard chamberId={chamber.id} chamberName={chamber.shortName} />
          <RemovedPeopleCard chamberId={chamber.id} />
        </div>

        <div className="flex flex-col gap-4">
          <AccreditationCard chamberId={chamber.id} chamberName={chamber.shortName} />

          <BrandingCard chamber={chamber} />

          <Card>
            <CardHeader title="At a glance" />
            <dl className="grid gap-2 [font-size:13px]">
              <KeyValue label="Document types">{chamber.documentTypeCount}</KeyValue>
              <KeyValue label="Operators">{chamber.userCount}</KeyValue>
              <KeyValue label="Exporters">{chamber.organisationCount}</KeyValue>
              <KeyValue label="Certificates">{chamber.documentCount}</KeyValue>
              <KeyValue label="Region">{chamber.region ?? '—'}</KeyValue>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * What this chamber is accredited to issue.
 *
 * Creating a document type in the platform catalogue does not make it issuable anywhere —
 * this is the second half. Withdrawal is refused by the server while the chamber has
 * requests of that type awaiting action, because those would become unapprovable and an
 * operator would be left with a queue they cannot clear.
 */
function AccreditationCard({ chamberId, chamberName }: { chamberId: string; chamberName: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const types = useQuery({
    queryKey: ['platform-document-types'],
    queryFn: () => api.get<DocumentTypeOption[]>('/api/console/platform/document-types'),
  });
  const enabled = useQuery({
    queryKey: ['chamber-document-types', chamberId],
    queryFn: () => api.get<Array<{ code: string }>>(`/api/console/platform/chambers/${chamberId}/document-types`),
  });

  const toggle = useMutation({
    mutationFn: (input: { documentTypeId: string; enabled: boolean }) =>
      api.post(
        `/api/console/platform/chambers/${chamberId}/document-types/${input.documentTypeId}`,
        { enabled: input.enabled },
      ),
    onSuccess: (_r, input) => {
      toast.success(
        input.enabled ? 'Accredited' : 'Withdrawn',
        input.enabled
          ? `${chamberName} still needs a stamping layout and a number format for it.`
          : undefined,
      );
      void queryClient.invalidateQueries({ queryKey: ['chamber-document-types', chamberId] });
      void queryClient.invalidateQueries({ queryKey: ['chamber-readiness', chamberId] });
      void queryClient.invalidateQueries({ queryKey: ['chambers'] });
    },
    onError: (error) =>
      toast.error(
        'Could not change accreditation',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const enabledCodes = new Set((enabled.data ?? []).map((t) => t.code));

  return (
    <Card id="accreditation" tabIndex={-1} className="scroll-mt-4 outline-none">
      <CardHeader
        title="Accredited to issue"
        hint={enabledCodes.size ? `${enabledCodes.size}` : undefined}
        description="Each one still needs a stamping layout and a number format before it can be issued."
      />
      {types.isLoading || enabled.isLoading ? (
        <Skeleton className="h-40" />
      ) : (
        <ul className="flex list-none flex-col gap-0 p-0">
          {(types.data ?? []).map((type) => (
            <li
              key={type.id}
              className="flex items-center gap-3 border-b border-[var(--pv-line)] py-2 last:border-b-0"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate [font-size:13px] font-medium">{type.name}</span>
                <span className="block truncate [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                  {type.agreement ?? 'Non-preferential'}
                </span>
              </span>
              <Switch
                checked={enabledCodes.has(type.code)}
                disabled={toggle.isPending}
                onCheckedChange={(on: boolean) => toggle.mutate({ documentTypeId: type.id, enabled: on })}
                aria-label={`${enabledCodes.has(type.code) ? 'Withdraw' : 'Accredit'} ${type.name}`}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * The creation wizard.
 *
 * A chamber is always created inactive: everything downstream — seals, layouts, numbering,
 * operators — has to exist before it can issue anything, and creating it live would open a
 * door onto an empty room.
 */
function CreateChamberDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [shortName, setShortName] = useState('');
  const [region, setRegion] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1F4D36');
  const [secondaryColor, setSecondaryColor] = useState('#C9DEA6');
  const [sandbox, setSandbox] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);

  const types = useQuery({
    queryKey: ['platform-document-types'],
    queryFn: () => api.get<DocumentTypeOption[]>('/api/console/platform/document-types'),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/console/platform/chambers', {
        slug,
        legalName,
        shortName,
        region: region || null,
        contactEmail,
        primaryColor,
        secondaryColor,
        sandbox,
        documentTypeCodes: codes,
      }),
    onSuccess: (result) => {
      toast.success(
        `${shortName} created`,
        'It starts offline. Work through the checklist, then take it live.',
      );
      void queryClient.invalidateQueries({ queryKey: ['chambers'] });
      onOpenChange(false);
      navigate(`/platform/chambers/${result.id}`);
    },
    onError: (error) =>
      toast.error('Could not create the chamber', error instanceof ApiError ? error.message : ''),
  });

  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors : {};
  const valid = slug.trim() && legalName.trim() && shortName.trim() && contactEmail.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[44rem]">
        <DialogHeader
          title="Add a chamber"
          description="A tenant with its own operators, seals, numbering and branding, isolated from every other. It is created offline."
        />
        <DialogBody>
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Legal name" required error={fieldErrors.legalName}>
                <Input
                  value={legalName}
                  onChange={(e) => {
                    setLegalName(e.target.value);
                    // A slug is derived until someone edits it, then it is theirs. It is
                    // in every URL and cannot be changed afterwards.
                    if (!slugTouched) setSlug(toSlug(e.target.value));
                  }}
                  placeholder="Business Chamber Queensland Limited"
                />
              </Field>
              <Field label="Short name" required help="What the console header shows." error={fieldErrors.shortName}>
                <Input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="Business Chamber QLD" />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field
                label="Slug"
                required
                help="Permanent. It appears in URLs and storage keys."
                error={fieldErrors.slug}
              >
                <Input
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(toSlug(e.target.value));
                  }}
                  className="pv-num"
                  placeholder="bcq"
                />
              </Field>
              <Field label="Region" error={fieldErrors.region}>
                <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Queensland" />
              </Field>
              <Field label="Contact email" required error={fieldErrors.contactEmail}>
                <Input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="certificates@example.org.au"
                />
              </Field>
            </div>

            <div>
              <p className="pv-eyebrow text-[var(--pv-faint)]">Branding</p>
              <div className="mt-2 flex flex-wrap items-center gap-4">
                <ColorField label="Primary" value={primaryColor} onChange={setPrimaryColor} />
                <ColorField label="Secondary" value={secondaryColor} onChange={setSecondaryColor} />
                <div className="flex items-center gap-2.5 rounded-[var(--pv-radius)] border border-[var(--pv-line)] px-3 py-2">
                  <span
                    aria-hidden
                    className="size-8 rounded-[var(--pv-radius-xs)]"
                    style={{ background: primaryColor, boxShadow: `inset 0 -4px 0 ${secondaryColor}` }}
                  />
                  <span className="[font-size:12px] text-[var(--pv-muted)]">Preview</span>
                </div>
              </div>
            </div>

            <div>
              <p className="pv-eyebrow text-[var(--pv-faint)]">
                Accredited to issue {codes.length > 0 && `· ${codes.length} selected`}
              </p>
              <p className="mt-1 [font-size:12.5px] text-[var(--pv-muted)]">
                Each one needs a stamping layout and a number format before the chamber can go
                live, so start with what they actually issue.
              </p>
              {types.isLoading ? (
                <Skeleton className="mt-2 h-32" />
              ) : (
                <div className="mt-2 grid max-h-56 gap-1 overflow-auto rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-2 sm:grid-cols-2">
                  {(types.data ?? []).map((type) => (
                    <label
                      key={type.id}
                      className="flex cursor-pointer items-start gap-2 rounded-[var(--pv-radius-xs)] px-2 py-1.5 hover:bg-[var(--pv-paper-sunk)]"
                    >
                      <Checkbox
                        checked={codes.includes(type.code)}
                        onCheckedChange={(checked) =>
                          setCodes((prev) =>
                            checked ? [...prev, type.code] : prev.filter((c) => c !== type.code),
                          )
                        }
                        aria-label={type.name}
                      />
                      <span className="min-w-0">
                        <span className="block truncate [font-size:13px] font-medium">{type.name}</span>
                        <span className="block truncate [font-size:11.5px] text-[var(--pv-faint)]">
                          {type.agreement ?? 'Non-preferential'}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <label className="flex cursor-pointer items-start gap-2.5 rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3">
              <Checkbox checked={sandbox} onCheckedChange={(c) => setSandbox(Boolean(c))} aria-label="Sandbox chamber" />
              <span>
                <span className="block [font-size:13px] font-medium">This is a sandbox chamber</span>
                <span className="block [font-size:12.5px] text-[var(--pv-muted)]">
                  Certificates it issues are watermarked and never enter a real number sequence.
                  Use it to let an integration partner rehearse against something safe.
                </span>
              </span>
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Check className="size-3.5" />}
            disabled={!valid}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create chamber
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2.5">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="size-9 cursor-pointer rounded-[var(--pv-radius-xs)] border border-[var(--pv-line)] bg-transparent p-0.5"
        aria-label={`${label} colour`}
      />
      <span>
        <span className="block [font-size:12px] text-[var(--pv-faint)]">{label}</span>
        <span className="pv-num block [font-size:12.5px] font-medium">{value.toUpperCase()}</span>
      </span>
    </label>
  );
}

interface ChamberUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
  hasPassword: boolean;
}

/**
 * Who runs this chamber, and inviting its first administrator.
 *
 * Every chamber-side checklist item (operators, seals, signatures, layouts, numbering) is
 * done by a Chamber Admin after they sign in — and a new chamber has none, so this is the
 * platform team's one step that unlocks the rest.
 */
interface RemovedPeople {
  users: Array<{ id: string; firstName: string; lastName: string; email: string; role: string; removedAt: string; anonymisedAt: string | null }>;
  signatories: Array<{ id: string; fullName: string; email: string | null; companyName: string; removedAt: string; anonymisedAt: string | null }>;
}

/**
 * People removed from this chamber, for a privacy request (T-144). Anonymising replaces a name
 * and email with "Former …" everywhere except the record of what they certified. Irreversible,
 * so it asks twice.
 */
function RemovedPeopleCard({ chamberId }: { chamberId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<string | null>(null);
  const people = useQuery({
    queryKey: ['removed-people', chamberId],
    queryFn: () => api.get<RemovedPeople>(`/api/console/platform/chambers/${chamberId}/removed-people`),
  });
  const anonymise = useMutation({
    mutationFn: (target: { kind: 'users' | 'signatories'; id: string }) =>
      api.post(`/api/console/platform/chambers/people/${target.kind}/${target.id}/anonymise`),
    onSuccess: () => {
      toast.success('Anonymised', 'Their name and email are gone from every screen.');
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['removed-people', chamberId] });
    },
    onError: (error) => toast.error('Could not anonymise', error instanceof ApiError ? error.message : ''),
  });
  const rows = [
    ...(people.data?.users ?? []).map((u) => ({ kind: 'users' as const, id: u.id, name: `${u.firstName} ${u.lastName}`, detail: `${u.email} · chamber staff`, removedAt: u.removedAt, anonymisedAt: u.anonymisedAt })),
    ...(people.data?.signatories ?? []).map((sig) => ({ kind: 'signatories' as const, id: sig.id, name: sig.fullName, detail: `${sig.companyName} · signatory`, removedAt: sig.removedAt, anonymisedAt: sig.anonymisedAt })),
  ];
  return (
    <Card>
      <CardHeader
        title="People who have left"
        description="Removed by the chamber or by Provenir support. Anonymise someone only on a privacy request; it cannot be undone."
      />
      {people.isLoading ? (
        <Skeleton className="h-20" />
      ) : rows.length === 0 ? (
        <p className="[font-size:13px] text-[var(--pv-muted)]">Nobody has been removed.</p>
      ) : (
        <ul className="flex list-none flex-col p-0">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-3 border-b border-[var(--pv-line)] py-2 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{r.name}</p>
                <p className="truncate [font-size:12.5px] text-[var(--pv-faint)]">{r.detail} · removed {new Date(r.removedAt).toLocaleDateString('en-AU')}</p>
              </div>
              {r.anonymisedAt ? (
                <Badge tone="neutral" size="sm">Anonymised</Badge>
              ) : (
                <Button
                  size="sm"
                  variant={confirm === r.id ? 'danger' : 'ghost'}
                  loading={anonymise.isPending && confirm === r.id}
                  onBlur={() => setConfirm((c) => (c === r.id ? null : c))}
                  onClick={() => (confirm === r.id ? anonymise.mutate({ kind: r.kind, id: r.id }) : setConfirm(r.id))}
                >
                  {confirm === r.id ? 'Anonymise for good?' : 'Anonymise'}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ChamberAdminsCard({ chamberId, chamberName }: { chamberId: string; chamberName: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const users = useQuery({
    queryKey: ['chamber-users', chamberId],
    queryFn: () => api.get<ChamberUser[]>(`/api/console/platform/chambers/${chamberId}/users`),
  });

  const invite = useMutation({
    mutationFn: () =>
      api.post(`/api/console/platform/chambers/${chamberId}/admins`, { firstName, lastName, email }),
    onSuccess: () => {
      toast.success('Invitation sent', `${email} will receive a link to set their password.`);
      setFirstName('');
      setLastName('');
      setEmail('');
      void queryClient.invalidateQueries({ queryKey: ['chamber-users', chamberId] });
      void queryClient.invalidateQueries({ queryKey: ['chambers'] });
    },
    onError: (error) =>
      toast.error('Could not send the invitation', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const admins = (users.data ?? []).filter((u) => u.role === UserRole.CHAMBER_ADMIN);
  const canSubmit = firstName.trim() && lastName.trim() && email.trim() && !invite.isPending;

  return (
    <Card id="chamber-admins" tabIndex={-1} className="scroll-mt-4 outline-none">
      <CardHeader
        title="Chamber administrators"
        description={`Items marked "Chamber admin" above are done by ${chamberName}'s administrator after they sign in. Invite them here.`}
      />
      {users.isLoading ? (
        <Skeleton className="h-16" />
      ) : admins.length > 0 ? (
        <ul className="mb-4 flex list-none flex-col gap-0 p-0">
          {admins.map((u) => (
            <li key={u.id} className="flex items-center gap-3 border-b border-[var(--pv-line)] py-2 last:border-b-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{u.firstName} {u.lastName}</p>
                <p className="[font-size:12.5px] text-[var(--pv-faint)]">{u.email}</p>
              </div>
              {!u.active ? (
                <Badge tone="neutral" size="sm">Deactivated</Badge>
              ) : u.hasPassword ? (
                <Badge tone="ok" size="sm" dot>Active</Badge>
              ) : (
                <Badge tone="warn" size="sm" dot>Invited</Badge>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-4 [font-size:13px] text-[var(--pv-muted)]">
          No administrator yet. Until one is invited, nobody can finish setting this chamber up.
        </p>
      )}
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) invite.mutate();
        }}
      >
        <Field label="First name" required>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Last name" required>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="Email address" required className="sm:col-span-2">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
        </Field>
        <div className="sm:col-span-2">
          <Button type="submit" variant={admins.length ? 'secondary' : 'primary'} disabled={!canSubmit} loading={invite.isPending}>
            {admins.length ? 'Invite another administrator' : 'Invite chamber administrator'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/**
 * The chamber's logo and colours — owned by the platform team, which is why it lives here
 * and not in the chamber's own configuration. The logo is a go-live check: without it the
 * console header, every email and the verification page fall back to the name as text.
 */
function BrandingCard({ chamber }: { chamber: ChamberRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [primaryColor, setPrimaryColor] = useState(chamber.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(chamber.secondaryColor);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['chambers'] });
    void queryClient.invalidateQueries({ queryKey: ['chamber-readiness', chamber.id] });
  };

  const upload = useMutation({
    mutationFn: async (file: File) =>
      api.post<{ logoUrl: string }>(`/api/console/platform/chambers/${chamber.id}/logo`, {
        filename: file.name,
        contentBase64: await toBase64(file),
      }),
    onSuccess: () => {
      toast.success('Logo saved', 'It now appears in the console, emails and the verification page.');
      refresh();
    },
    onError: (error) =>
      toast.error('Could not save the logo', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const saveColours = useMutation({
    mutationFn: () =>
      api.patch(`/api/console/platform/chambers/${chamber.id}/branding`, { primaryColor, secondaryColor }),
    onSuccess: () => {
      toast.success('Colours saved');
      refresh();
    },
    onError: (error) =>
      toast.error('Could not save the colours', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Use a PNG or JPEG', 'Export the logo as a PNG, ideally with a transparent background.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('That image is too large', 'Use one under 2MB — around 600 pixels wide is plenty.');
      return;
    }
    upload.mutate(file);
  };

  const coloursChanged =
    primaryColor.toUpperCase() !== chamber.primaryColor.toUpperCase() ||
    secondaryColor.toUpperCase() !== chamber.secondaryColor.toUpperCase();

  return (
    <Card id="branding" tabIndex={-1} className="scroll-mt-4 outline-none">
      <CardHeader title="Branding" />

      <div className="flex flex-col gap-2">
        <p className="pv-eyebrow text-[var(--pv-faint)]">Logo</p>
        {chamber.logoUrl ? (
          <div className="flex h-20 items-center justify-center rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-3">
            <img
              src={chamber.logoUrl}
              alt={`${chamber.shortName} logo`}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        ) : (
          <div className="flex h-20 items-center justify-center rounded-[var(--pv-radius)] border border-dashed border-[var(--pv-line-strong)] px-3 text-center [font-size:12.5px] text-[var(--pv-muted)]">
            No logo yet — needed before going live
          </div>
        )}
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg"
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <Button
          variant={chamber.logoUrl ? 'secondary' : 'primary'}
          size="sm"
          leadingIcon={<Upload className="size-3.5" />}
          loading={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {chamber.logoUrl ? 'Replace logo' : 'Upload logo'}
        </Button>
        <p className="[font-size:12px] leading-snug text-[var(--pv-faint)]">
          PNG or JPEG under 2MB. A PNG with a transparent background looks best on both light
          and dark headers.
        </p>
      </div>

      <div className="mt-4 flex flex-col gap-2 border-t border-[var(--pv-line)] pt-4">
        <p className="pv-eyebrow text-[var(--pv-faint)]">Colours</p>
        <div className="flex flex-wrap items-center gap-4">
          <ColorField label="Primary" value={primaryColor} onChange={setPrimaryColor} />
          <ColorField label="Secondary" value={secondaryColor} onChange={setSecondaryColor} />
          <span
            aria-hidden
            className="size-9 rounded-[var(--pv-radius-xs)]"
            style={{ background: primaryColor, boxShadow: `inset 0 -5px 0 ${secondaryColor}` }}
          />
        </div>
        {coloursChanged && (
          <div className="flex gap-2">
            <Button size="sm" variant="primary" loading={saveColours.isPending} onClick={() => saveColours.mutate()}>
              Save colours
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setPrimaryColor(chamber.primaryColor);
                setSecondaryColor(chamber.secondaryColor);
              }}
            >
              Cancel
            </Button>
          </div>
        )}
        <p className="[font-size:12px] leading-snug text-[var(--pv-faint)]">
          Applied at runtime, so the chamber's console and emails carry its brand without a
          separate build. Contrast is validated when it is applied.
        </p>
      </div>
    </Card>
  );
}

/** Your own chamber's checklist. The Chamber Admin signs off that it is ready. */
export function ChamberReadiness() {
  const { user } = useSession();
  const query = useQuery({
    queryKey: ['own-readiness'],
    queryFn: () => api.get<Readiness>('/api/console/chamber/readiness'),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Ready to issue?"
        lede="What has to be in place before your chamber can issue real certificates. The platform evaluates this list itself and re-checks it at activation, so a green tick here means the server agrees."
      />
      <Card>
        <ReadinessChecklist
          readiness={query.data}
          loading={query.isLoading}
          audience={user?.role === UserRole.PLATFORM_SUPER_ADMIN ? 'platform' : 'chamber'}
        />
      </Card>

      {user?.role === UserRole.CHAMBER_ADMIN && <SignatoryPolicyCard />}
    </div>
  );
}

interface SignatoryPolicy {
  validityMonths: number;
  /** How much notice before a specimen lapses. */
  warningDays: number;
  specimensOnFile: number;
  expiringWithinAMonth: number;
  expired: number;
}

/**
 * How long an exporter's specimen signature stays valid here.
 *
 * A chamber policy rather than a platform rule: some re-verify annually, some every two
 * years, some align it to a membership term. The window is stamped onto each specimen
 * when it is accepted, so a change applies going forward and never re-dates a record an
 * operator has already relied on — which the card says, because the opposite is what
 * people assume.
 */
function SignatoryPolicyCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [months, setMonths] = useState<number | null>(null);
  const [warningDays, setWarningDays] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['signatory-policy'],
    queryFn: () => api.get<SignatoryPolicy>('/api/console/configuration/signatory-policy'),
  });

  const save = useMutation({
    mutationFn: (next: { validityMonths: number; warningDays: number }) =>
      api.post('/api/console/configuration/signatory-policy', next),
    onSuccess: (_r, next) => {
      toast.success(
        `Specimens are now accepted for ${next.validityMonths} months`,
        `Both parties are warned ${next.warningDays} days before one lapses. Specimens already on file keep the window they were given.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['signatory-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['exporters'] });
    },
    onError: (error) =>
      toast.error('Could not change the policy', error instanceof ApiError ? error.message : ''),
  });

  if (query.isLoading) return <Skeleton className="h-40" />;
  const policy = query.data;
  if (!policy) return null;

  const value = months ?? policy.validityMonths;
  const notice = warningDays ?? policy.warningDays;
  const dirty = value !== policy.validityMonths || notice !== policy.warningDays;

  return (
    <Card>
      <CardHeader
        title="Specimen signatures"
        description="How long a specimen signature an exporter provides stays valid before your chamber asks for a fresh one."
      />
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <Field label="Valid for" help="Between 1 and 120 months.">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={120}
              value={value}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="pv-num w-24"
            />
            <span className="[font-size:13px] text-[var(--pv-muted)]">months</span>
          </div>
        </Field>

        {/* Beside the window it belongs to: the chamber that chose two years is the one
            that should choose how much warning it wants at the end of them. */}
        <Field
          label="Warn before it lapses"
          help="Your chamber and the exporter are both told — neither can fix it alone."
        >
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={180}
              value={notice}
              onChange={(e) => setWarningDays(Number(e.target.value))}
              className="pv-num w-24"
            />
            <span className="[font-size:13px] text-[var(--pv-muted)]">days</span>
            {dirty && (
              <Button
                variant="primary"
                size="sm"
                loading={save.isPending}
                onClick={() => save.mutate({ validityMonths: value, warningDays: notice })}
              >
                Save
              </Button>
            )}
          </div>
        </Field>

        <dl className="flex flex-wrap gap-x-8 gap-y-3 [font-size:12.5px]">
          <div>
            <dt className="text-[var(--pv-faint)]">On file</dt>
            <dd className="pv-num font-medium">{policy.specimensOnFile}</dd>
          </div>
          <div>
            <dt className="text-[var(--pv-faint)]">Expiring within a month</dt>
            <dd className="pv-num font-medium">{policy.expiringWithinAMonth}</dd>
          </div>
          <div>
            <dt className="text-[var(--pv-faint)]">Expired</dt>
            <dd className="pv-num font-medium">{policy.expired}</dd>
          </div>
        </dl>
      </div>

      <p className="mt-3 [font-size:12px] leading-snug text-[var(--pv-faint)]">
        Changing this applies to specimens accepted from now on. Records already on file keep
        the window they were given, so shortening it cannot retroactively invalidate a
        specimen an operator has already relied on.
      </p>
    </Card>
  );
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}
